// Centralised Finnish UI strings. English variant lands in M11+; for now we
// ship Finnish-only since the operators are all native speakers.

export const fi = {
  app: {
    title: 'TAKSIKORTTI',
    subtitle: 'Korttiveloitusten valvonta',
    skip: 'Siirry sisältöön',
    menu: 'Valikko',
    notFoundTitle: 'Sivua ei löytynyt',
    notFoundHint: 'Tarkista osoite tai palaa etusivulle.',
    notFoundCta: 'Etusivulle',
  },
  nav: {
    dashboard: 'Yleiskatsaus',
    cases: 'Tapaukset',
    palvelutunnukset: 'Palvelutunnukset',
    import: 'Tuonti',
    korttitoimitus: 'Korttitoimitus',
    admin: 'Excel-migraatio',
    logout: 'Kirjaudu ulos',
  },
  korttitoimitus: {
    title: 'Korttitoimitus',
    subtitle: 'Muotoile neljännesvuoden korttitoimitusaineisto valmiiksi',
    help: 'Yhdistää Kertakortti- ja Maksuaikakortti-välilehdet yhdeksi taulukoksi, lisää tuote-sarakkeen (KEKO/MAKO), poistaa HUS-asiakkaan rivit, ohittaa TaksikorttiPLUS-välilehden ja järjestää asiakasnumeron mukaan. Lataa raaka .xlsx — saat valmiin tiedoston takaisin.',
    drop: 'Vedä tai napsauta — korttitoimitus .xlsx',
    hint: '.xlsx · max 50 MB',
    processing: 'Muotoillaan…',
    doneTitle: '✓ Tiedosto valmis',
    download: 'Lataa uudelleen',
    outputRows: 'Rivejä tuloksessa',
    removedHus: 'HUS-rivejä poistettu',
    skippedPlus: 'TaksikorttiPLUS ohitettu',
    perTab: 'Välilehdet',
  },
  auth: {
    notSignedIn: 'Et ole kirjautunut sisään.',
    signIn: 'Kirjaudu Microsoft-tilillä',
  },
  palvelutunnukset: {
    title: 'Palvelutunnukset',
    subtitle: 'Palvelutunnukset, joilla on useita tapauksia — toistuvat rikkomukset',
    empty: 'Ei palvelutunnuksia näkyvissä.',
    emptyNoMatches: 'Suodattimet eivät tuottaneet osumia.',
    filters: {
      search: 'Hae palvelutunnus',
      repeatOnly: 'Vain toistuvat (≥ 2 tapausta)',
    },
    columns: {
      palvelutunnus: 'Palvelutunnus',
      incidents: 'Tapauksia',
      cards: 'Uniikkeja kortteja',
      lastCase: 'Viimeisin tapaus',
    },
    count: (n: number) => (n === 1 ? '1 palvelutunnus' : `${n} palvelutunnusta`),
  },
  duplicates: {
    title: 'Tuplaveloitukset',
    subtitle: 'Mahdolliset tuplaveloitukset — sama asiakas, palvelutunnus ja kortti ≤ 15 min sisällä',
    empty: 'Ei tuplaveloituksia näkyvissä.',
    emptyNoMatches: 'Suodattimet eivät tuottaneet osumia.',
    filters: {
      search: 'Hae kortti / asiakas / palvelutunnus',
      showDismissed: 'Näytä hylätyt',
    },
    columns: {
      reviewStatus: 'Tila',
      customer: 'Asiakas',
      palvelutunnus: 'Palvelutunnus',
      card: 'Kortti',
      count: 'Veloituksia',
      refunds: 'Hyvityksiä',
      net: 'Netto',
      totalAmount: 'Summa €',
      firstAt: 'Ensimmäinen',
      lastAt: 'Viimeinen',
      comment: 'Kommentti',
      actions: 'Toiminnot',
    },
    member: {
      time: 'Aika',
      receipt: 'Kuitti',
      amount: 'Hinta €',
      palvelutunnus: 'Palvelutunnus',
      driver: 'Kuljettaja',
    },
    count: (n: number) => (n === 1 ? '1 ryhmä' : `${n} ryhmää`),
    staleToast: 'Ryhmää muokattiin samalla — lataa uudestaan',
  },
  cardType: {
    kertakortti: 'Kertakortti',
    taksikortti: 'Taksikortti',
    taksikortti_plus: 'TaksikorttiPLUS',
    tuntematon: 'Tuntematon',
  } satisfies Record<string, string>,
  worklist: {
    title: 'Tapaukset',
    subtitle: 'Kertakorttirikkomukset ja tuplaveloitukset yhdessä näkymässä',
    empty: 'Ei tapauksia näkyvissä.',
    emptyNoMatches: 'Suodattimet eivät tuottaneet osumia.',
    kind: {
      case: 'Tapaus',
      duplicate: 'Tuplaveloitus',
    } satisfies Record<string, string>,
    columns: {
      card: 'Kortti',
      customer: 'Asiakas / yritys',
      palvelutunnus: 'Palvelutunnus',
      status: 'Tila',
      charges: 'Veloitukset',
      invoice: 'Laskutus',
      payment: 'Tilitys',
      comment: 'Kommentti',
      firstAt: 'Ensim.',
      lastAt: 'Viim.',
    },
    filters: {
      search: 'Hae kortti / asiakas / palvelutunnus',
      cardType: 'Korttityyppi',
      kind: 'Tyyppi',
      showDone: 'Näytä valmiit',
      all: 'Kaikki',
      clear: 'Tyhjennä suodattimet',
      invoiceState: 'Laskutustila',
      paymentState: 'Tilitystila',
    },
    // One-click triage presets (set the state/invoice filters as a bundle).
    quick: {
      open: 'Avoimet',
      review: 'Tarkistettavat',
      soon: 'Pian laskutetaan',
      done: 'Valmiit',
    },
    newBadge: 'Uusi',
    newBadgeTitle: (created: string) => `Lisätty ${created} — ei vielä käsitelty`,
    count: (n: number) => (n === 1 ? '1 kohde' : `${n} kohdetta`),
    showing: (shown: number, total: number) => `Näytetään ${shown} / ${total}`,
    prev: 'Edellinen',
    next: 'Seuraava',
    openCase: 'Avaa →',
  },
  dashboard: {
    title: 'Yleiskatsaus',
    subtitle: 'Mitä vaatii huomiota juuri nyt.',
    openCases: 'Avoimet tapaukset',
    reviewRequired: 'Tarkistettavat tuplat',
    pendingDups: 'Tarkistamattomat tuplat',
    repeatTerminals: 'Toistuvat palvelutunnukset',
    lastImport: 'Viimeisin tuonti',
    noImports: 'Ei tuonteja',
    triageTitle: 'Vaatii tarkistusta',
    triageEmpty: 'Ei tarkistettavia kohteita.',
    viewAll: 'Näytä kaikki →',
  },
  dupDetail: {
    backToList: '← Tapaukset',
    members: 'Veloitukset ryhmässä',
    timeline: 'Aikajana',
    noEvents: 'Ei tapahtumia.',
    comment: 'Kommentti',
    summary: (count: number, refunds: number, total: string) =>
      `${count} veloitusta · ${refunds} hyvitystä netotettu · yhteensä ${total} €`,
  },
  invoice: {
    laskutus: 'Laskutus',
    tilitys: 'Tilitys',
    billedTimes: (n: number) => `Laskutettu ${n}×`,
    paidTimes: (n: number) => `Maksettu ${n}×`,
    autoResolved: 'Ratkaistu automaattisesti — ei laskutettu/maksettu',
    values: {
      Laskutettu: 'Laskutettu',
      'Hyväksytty laskutukseen': 'Hyväksytty',
      'Ei voida hyväksyä laskutukseen': 'Ei voida hyväksyä',
      'Ei laskuteta': 'Ei laskuteta',
    } as Record<string, string>,
  },
  // Aggregated per-row invoice/payment state (worklist filter + badge).
  chargeState: {
    approved: 'Pian',
    active: 'Valmis',
    pending: 'Odottaa',
    blocked: 'Estetty',
    none: '—',
  } as Record<string, string>,
  status: {
    open: 'Avoin',
    query_sent: 'Kysely lähetetty',
    reminder_sent: 'Muistutus lähetetty',
    closed: 'Suljettu',
  } satisfies Record<string, string>,
  // Unified state vocabulary shown across cases + duplicates (see StateBadge).
  state: {
    avoin: 'Avoin',
    kysely: 'Kysely',
    muistutus: 'Muistutus',
    tarkistettava: 'Tarkistettava',
    valmis: 'Valmis',
  } satisfies Record<string, string>,
  closedReason: {
    resolved: 'Selvitetty',
    no_reply: 'Ei vastausta',
    not_applicable: 'Ei aihetta',
  } satisfies Record<string, string>,
  caseDetail: {
    backToList: '← Tapaukset',
    reopen: 'Avaa uudelleen',
    closeCase: 'Sulje tapaus',
    actionDone: 'Päivitetty',
    incident: (n: number, total: number) => `Incident ${n} / ${total}`,
    trips: 'Matkat',
    tripsHeader: (b: number, r: number, e: number, c: number) =>
      `${b} veloitettavaa · ${r} hyvitystä · ${e} ei laskuteta · ${c} käsitelty`,
    previousIncidents: 'Aiemmat tapaukset tällä kortilla',
    timeline: 'Aikajana',
    staleToast: 'Tapausta muokattiin samalla — lataa uudestaan',
    reload: 'Lataa uudestaan',
    tripActions: {
      send_query: 'Kirjaa kysely',
      send_reminder: 'Kirjaa muistutus',
      close: 'Sulje',
      reopen: 'Avaa uudelleen',
    },
    tripStatus: {
      open: 'Avoin',
      query_sent: 'Kysely lähetetty',
      reminder_sent: 'Muistutus lähetetty',
      closed: 'Suljettu',
    } satisfies Record<string, string>,
    closeTripTitle: 'Sulje matka',
    closeTripSave: 'Sulje',
    closeTripCancel: 'Peruuta',
    closeTripReason: 'Lopputulos',
    closeTripNotes: 'Muistiinpanot (valinnainen)',
    caseNotes: 'Tapauksen muistiinpanot',
    caseNotesPlaceholder: 'Yleiset muistiinpanot tästä tapauksesta…',
    caseNotesSave: 'Tallenna',
    caseNotesSaved: 'Tallennettu',
    tripNotesTitle: 'Matkan muistiinpanot',
    tripNotesEdit: 'Muokkaa',
    tripNotesPlaceholder: 'Muistiinpanot tästä matkasta…',
    tripNotesSave: 'Tallenna',
    tripNotesCancel: 'Peruuta',
  },
  import: {
    title: 'Tuo CSV',
    columnsHelpTitle: 'CSV:ssä tulee olla seuraavat sarakkeet',
    columnsHelpHint: 'Sarakkeiden järjestyksellä ei ole väliä, mutta nimet on oltava täsmälleen samat. Erotin on puolipiste (;).',
    requiredColumns: [
      'Asiakasnumero',
      'Asiakas',
      'Kuittinumero',
      'Korttinumero',
      'Palvelutunnus',
      'Autonumero',
      'Kuljettajanumero',
      'Matkan hinta',
      'Matkan alkamisaika',
      'Matkan päättymisaika',
      'Myyntilaskutustila',
      'Tilitystila',
      'Tilaustiedot',
    ] as const,
    drop: 'Vedä tai napsauta — yksi CSV-tiedosto',
    dropAccept: 'Pudota tiedosto tähän',
    dropReject: 'Vain CSV-tiedostoja hyväksytään',
    uploading: 'Ladataan tiedostoa…',
    importing: 'Tuonti käynnissä — älä sulje sivua',
    importingDetail: 'Isolla tiedostolla voi kestää useita minuutteja. Pysy sivulla kunnes näet "Tuonti valmis".',
    importingProgress: (done: number, total: number, pct: number) =>
      `${done.toLocaleString('fi-FI')} / ${total.toLocaleString('fi-FI')} riviä · ${pct} %`,
    importingFile: (name: string) => `Käsitellään: ${name}`,
    pollingFallback: 'Yhteys palvelimeen katkesi, mutta tuonti jatkuu taustalla. Tarkistetaan tila — älä sulje sivua.',
    pollingTimedOut: 'Tuontia ei saatu vahvistettua 15 minuutissa. Tarkista "Viimeisimmät tuonnit" -listasta tai yritä uudestaan.',
    result: 'Tuonti valmis',
    uploadedTitle: '✓ Tiedosto ladattu',
    uploadedBackground: 'Käsittely jatkuu taustalla — voit poistua sivulta. Seuraa edistymistä "Viimeisimmät tuonnit" -listasta.',
    resultDoneTitle: '✓ Tuonti valmis',
    parseFailuresAlertTitle: (n: number) => `${n} riviä jäi pois — virhe tiedostossa`,
    parseFailuresAlertHint: 'Nämä rivit eivät päätyneet järjestelmään. Korjaa lähdetiedosto tai tarkista että puuttuvat tiedot ovat tahallisia.',
    imported: 'Uusia matkoja',
    skipped: 'Aiemmin tuotuja',
    failed: 'Virheellisiä rivejä',
    newCases: 'Uusia tapauksia',
    recent: 'Viimeisimmät tuonnit',
    statusDone: 'Valmis',
    statusRunning: 'Käsitellään',
    statusInterrupted: 'Keskeytynyt',
    headerErrorTitle: 'CSV:n sarakkeet ovat virheelliset',
    headerErrorMissing: 'Puuttuvat sarakkeet:',
    headerErrorUnexpected: 'Tunnistamattomia sarakkeita (mahdollinen kirjoitusvirhe?):',
    headerErrorHint: 'Tarkista, että sarakkeiden nimet on kirjoitettu täsmälleen oikein ja että erotin on puolipiste (;).',
    headerError: (missing: string[]) => `CSV:stä puuttuu sarake: ${missing.join(', ')}`,
    errorTitle: 'Tuonti epäonnistui',
    errorHint: 'Tarkista tiedosto ja yritä uudestaan. Jos virhe toistuu, ota yhteyttä ylläpitoon.',
    allDuplicates: 'Tiedoston kaikki rivit oli jo tuotu aiemmin — ei uusia matkoja.',
  },
  errors: {
    generic: 'Tapahtui virhe — yritä uudestaan.',
    stale: 'Kohdetta muokattiin samalla — lataa uudestaan.',
    illegalTransition: 'Tämä toiminto ei ole sallittu nykyisestä tilasta.',
    conflictingOpen: 'Kortilla on jo toinen avoin tapaus.',
    retry: 'Yritä uudestaan',
    title: 'Lataaminen epäonnistui',
    fileRejected: 'Tiedostoa ei hyväksytty — tarkista tiedostotyyppi ja koko (max 50 MB).',
    byCode: {
      stale_version: 'Kohdetta muokattiin samalla — lataa uudestaan.',
      conflicting_open_case: 'Kortilla on jo toinen avoin tapaus.',
      illegal_transition: 'Tämä toiminto ei ole sallittu nykyisestä tilasta.',
      not_found: 'Kohdetta ei löytynyt.',
      forbidden: 'Ei käyttöoikeutta tähän toimintoon.',
      unauthenticated: 'Kirjautuminen vaaditaan.',
      bad_request: 'Virheellinen pyyntö.',
    } as Record<string, string>,
  },
};

export type Strings = typeof fi;
export const t = fi;
